import React, { useState } from 'react';
import classNames from 'classnames';
import Icon, { IconName } from 'app/common/components/controls/icon';
import { ComponentWrapper } from 'app/src/components/ComponentWrapper';
import { LinkButton } from 'app/common/components/controls/button/button';
import { UpgradePromptPlanSelectionOverlay } from './UpgradePromptPlanSelection';
import RouterLink from 'app/common/components/singlespa/router-link';
import styles from './UpgradePromptFull.less';
import { dontUpsell } from '@trello/config';
import { isDesktop } from '@trello/browser';

/**
 * UpgradePromptFull props
 *
 * content: prompt copy, optionally rendered
 * cta: call to action of prompt, navigates user within the page
 * ctaLink: link for call to action
 * ctaOnClick: function expected to fire side-effects like a trackUe event,
 *             fired on click of cta
 * hidePlanOverlay: will use a link for CTA instead of showing the overlay
 * headline: prompt header
 * iconName: name of icon
 * isDarkTheme: controls dark theme
 * isDismissable: optonially renders close button
 * onDismiss: function expected to unmount prompt and make dismissal persistent for the user
 */

interface UpgradePromptFullProps {
  className?: string;
  content?: string;
  cta: string;
  ctaLink?: string;
  ctaOnClick?: () => void;
  overlayOnContinue?: () => void;
  hidePlanOverlay?: boolean;
  headline: string;
  iconName: IconName;
  isDarkTheme?: boolean;
  isDismissable?: boolean;
  onDismiss?: () => void;
  allowUpsell?: boolean;
}

export const UpgradePromptFull: React.FunctionComponent<UpgradePromptFullProps> = ({
  allowUpsell,
  className,
  content,
  cta,
  ctaLink,
  ctaOnClick,
  overlayOnContinue,
  headline,
  hidePlanOverlay,
  iconName,
  isDarkTheme,
  isDismissable,
  onDismiss,
}) => {
  const [showPlanOverlay, togglePlanOverlay] = useState(false);
  const closeOverlay = () => togglePlanOverlay(false);
  const openOverlay = () => togglePlanOverlay(true);
  const onSkip = () => {
    if (!hidePlanOverlay) {
      closeOverlay();
    }

    if (onDismiss) {
      onDismiss();
    }
  };
  const onClickCTA = () => {
    if (!hidePlanOverlay) {
      openOverlay();
    }

    if (ctaOnClick) {
      ctaOnClick();
    }
  };

  const onContinueOverlay = () => {
    if (overlayOnContinue) {
      overlayOnContinue();
    }

    closeOverlay();
  };

  const promptClasses = classNames(
    styles.upgradePromptFull,
    isDarkTheme
      ? styles.upgradePromptFullDarkTheme
      : styles.upgradePromptFullLightTheme,
    className,
  );

  const contentBlock = content && (
    <div className={styles.upgradePromptFullContent}>{content}</div>
  );

  const closeButton = isDismissable && (
    <button
      className={styles.upgradePromptFullCloseButtonWrapper}
      onClick={onDismiss}
    >
      <Icon
        className={styles.upgradePromptFullCloseButton}
        name={IconName.Close}
        small={true}
      />
    </button>
  );

  const LinkComponent = ctaLink === '/business-class' ? 'a' : RouterLink;

  if (dontUpsell && !allowUpsell) {
    return null;
  }

  return (
    <div className={promptClasses}>
      <div className={styles.upgradePromptFullHeadline}>{headline}</div>
      {contentBlock}
      {hidePlanOverlay ? (
        <LinkComponent
          href={ctaLink}
          className={styles.upgradePromptFullCTA}
          onClick={onClickCTA}
          target={
            isDesktop() && ctaLink === '/business-class' ? '_blank' : undefined
          }
        >
          {cta}
        </LinkComponent>
      ) : (
        <LinkButton
          onClick={onClickCTA}
          className={styles.upgradePromptFullCTA}
        >
          {cta}
        </LinkButton>
      )}
      {!hidePlanOverlay && showPlanOverlay && (
        <UpgradePromptPlanSelectionOverlay
          onClose={() => {
            togglePlanOverlay(false);

            if (onDismiss) {
              onDismiss();
            }
          }}
          onContinue={onContinueOverlay}
          onSkip={onSkip}
          ctaLink={ctaLink}
        />
      )}
      <div className={styles.upgradePromptFullIconBackground}>
        <Icon
          className={styles.upgradePromptFullIcon}
          name={iconName}
          small={true}
        />
      </div>
      <div className={styles.upgradePromptFullIconClippedBackground} />
      {closeButton}
    </div>
  );
};

export const UpgradePromptFullConnected: React.FunctionComponent<UpgradePromptFullProps> = (
  props,
) => (
  <ComponentWrapper>
    <UpgradePromptFull {...props} />
  </ComponentWrapper>
);
